/**
 * compare-cell.js — 比較表「儲存格值」共用渲染器（product-compare 與 product-detail 共用）
 *
 * 為什麼抽出：product-compare（多產品比較表）與 product-detail（單產品規格表）需要把同一份
 * 規格資料（product-compare-items.js 的 COMPARE_SPEC_ROWS）以「完全相同的視覺」呈現——
 * bool 打勾 icon、證據分級 badge、verified 綠勾 +「已驗證」文字。兩頁各自複製一份 renderer
 * 會 drift，故把 cell 渲染抽成單一真相源：兩頁 import 同一組函式 → 產出的 class 名
 * （.compare-table__bool/__check/__verified/__verified-label/__evidence + .badge--success/--info）
 * 完全一致，CSS 一份共用
 * （renderer 樣式定義在 styles/pages/product-compare.css，未 scope 在 .page-product-compare 下，
 * 故 product-detail 頁也吃得到）。
 *
 * 主要 export：renderCellValue（依 row.type/key 把單一值轉顯示 HTML）、renderEvidence、
 *   CHECK_ICON、esc（HTML escape）。EVIDENCE_BADGE 為 renderEvidence 私有對映表，不 export。
 *
 * 行為契約：與抽出前的 product-compare.js 逐字一致（純搬移、零行為變更）。改 cell 視覺前先確認
 *   兩頁皆受影響（grep renderCellValue 求證消費端）。
 */

/** HTML escape，防止假資料字串內含 < > & 破壞 markup（prototype 防護慣例）。 */
export function esc(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export const CHECK_ICON =
  '<svg class="compare-table__check" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
  '<path d="M5 13l4 4L19 7" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

// 證據分級 → badge：依分級碼前綴對映 badge atom 變體。VE（實證審核章）→ success、
// CI（議題洞察章）→ info。碼放 badge 內（短，不破 nowrap），章名文字緊接 badge 後。
const EVIDENCE_BADGE = {
  VE: { variant: 'badge--success', code: 'VE' },
  CI: { variant: 'badge--info', code: 'CI' },
};

/**
 * 把 evidenceLevel 字串（如「VE · 實證審核章」）拆成 badge（分級碼）+ 緊接的章名文字。
 * 碼用 startsWith 偵測，避免 badge 內塞整串撐破 nowrap；無法辨識的值退回純文字。
 */
export function renderEvidence(value) {
  const raw = String(value);
  const key = Object.keys(EVIDENCE_BADGE).find((k) => raw.startsWith(k));
  if (!key) return esc(value);
  const { variant, code } = EVIDENCE_BADGE[key];
  // 章名 = 原字串去掉開頭碼 + 分隔符（· / 空白），緊接 badge 之後顯示。
  const label = raw.replace(new RegExp(`^${code}\\s*[·・]?\\s*`), '');
  return (
    `<span class="compare-table__evidence">` +
    `<span class="badge ${variant}">${esc(code)}</span>` +
    (label ? `<span class="compare-table__evidence-label">${esc(label)}</span>` : '') +
    `</span>`
  );
}

/** 依 spec row 型別把單一 cell 值轉成顯示 HTML。 */
export function renderCellValue(row, value) {
  if (row.type === 'bool') {
    // 螢幕報讀文字依列義（如衛福部核准列讀「已核准」），未指定則退回通用語。
    const yesLabel = row.boolYes || '支援';
    const noLabel = row.boolNo || '不支援';
    return value
      ? `<span class="compare-table__bool compare-table__bool--yes">${CHECK_ICON}<span class="visually-hidden">${esc(yesLabel)}</span></span>`
      : `<span class="compare-table__bool compare-table__bool--no" aria-label="${esc(noLabel)}">—</span>`;
  }
  if (row.type === 'verified') {
    // 平台審核「已驗證」標記：綠勾（同 bool 的 teal check 元件）+ 緊接「可見」文字。
    // 與 bool 的差異：意義由 visible 文字承載（非 visually-hidden），故 ✓ icon 保持 aria-hidden、
    // 不另加 SR span（避免文字 + SR span 雙重報讀）。false（未驗證）退回 dash，比照 bool--no。
    if (value) {
      const label = row.visibleLabel || '已驗證';
      return (
        `<span class="compare-table__verified">${CHECK_ICON}` +
        `<span class="compare-table__verified-label">${esc(label)}</span></span>`
      );
    }
    return '<span class="compare-table__bool compare-table__bool--no" aria-hidden="true">—</span>';
  }
  // 證據分級列：碼用 badge 呈現，章名緊接其後。
  if (row.key === 'evidenceLevel') {
    return renderEvidence(value);
  }
  return esc(value);
}
